<template>
    <v-container class="max-height" fluid>
        <v-row>
            <v-col cols="12">
                <h1 class="text-start">Driver Report</h1>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="2" sm="4">
                <company-select v-model="formData['company']" :items="companiesList"
                          density="compact" label="Select Company"
                          @update:model-value="()=>{fetchReport();fetchAssetList();fetchDriversList();}"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="formData['assets']" :items="assetsList" chips clearable
                          density="compact" item-title="label" item-value="id" label="Select Asset(s)" multiple
                          @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="formData['driver']" :items="driversList" clearable
                          density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id"
                          label="Select Driver" @update:model-value="fetchReport"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['start_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="Start Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['start_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-date-input
                    v-model="formData['end_date']"
                    append-inner-icon="$calendar"
                    clearable
                    density="compact"
                    hide-actions
                    label="End Date"
                    prepend-icon=""
                    @click:clear="()=>{formData['end_date']=null;fetchReport()}"
                    @update:model-value="fetchReport()"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="6">
                <Doughnut :data="{
                    labels: ['Avg. Driving Time', 'Avg. Idle Time'],
                    datasets: [{
                        data: [
                            parseFloat(stats['avg_duration']??0)/60,
                            parseFloat(stats['avg_idle_time']??0)/60
                            ],
                        backgroundColor: ['#849e00', '#d70000'],
                    }]}" :options="chartOptions"/>
            </v-col>
            <v-col class="position-relative" cols="6">
                <Doughnut :data="efficiencyDataset" :options="efficiencyChartOptions"/>
                <div class="efficiency-center-label">
                    <div class="text-h6 font-weight-bold">{{ efficiencyPercentage }}%</div>
                    <div class="caption">Efficiency</div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <h2 class="mt-5 text-start">Trips</h2>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Start Location</th>
                        <th>End Location</th>
                        <th>Distance</th>
                        <th>Total Duration</th>
                        <th>Idle Duration</th>
                        <th>Idle Counts</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in reportData" v-if="reportData.length" class="text-left">
                        <td>{{ formatDateTime(item.start_time) }}</td>
                        <td>{{ formatDateTime(item.end_time) }}</td>
                        <td>{{ item.start_location_text ?? (`${item.start_lat}, ${item.start_lon}`) }}</td>
                        <td>{{ item.end_location_text ?? (`${item.end_lat}, ${item.end_lon}`) }}</td>
                        <td>{{ strOperations().unitifyDistance(item.distance_km, 'distance', 'km') }}</td>
                        <td>{{ new Date(item.duration * 1000).toISOString().slice(11, 19) }}</td>
                        <td>{{ new Date(item.idle_time_total_sec * 1000).toISOString().slice(11, 19) }}</td>
                        <td>{{ item.total_idles }}</td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" class="text-center" colspan="8">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="8">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>


        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchReport"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {Pie, Doughnut} from "vue-chartjs";
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js';
import {useAuthStore} from "@/stores/authStore";
import {strOperations, useNavigation} from "@/composables/globals";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import CompanySelect from "@/components/globals/company-select.vue";

Chart.register(...registerables);

const {user} = useAuthStore();
const reportData = ref([]);
const totalPages = ref(1);
const loading = ref(false);
const currentPage = ref(1);
const assetsList = ref([]);
const driversList = ref([]);
const companiesList = ref([]);
const {navigateTo} = useNavigation();
const {showSnackbar} = useSnackbarStore();
const stats = ref({
    "count": 0,
    "avg_duration": 0,
    "avg_dile_count": 0,
    "avg_idle_time": 0
});
const formData = reactive({
    company: ref(user.type !== 'admin' ? user.company.id : null),
    start_date: null,
    end_date: null,
    driver: null,
    assets: null,
});

const chartOptions = {
    cutout: '45%', // default ~50%, increase for a thinner ring
    responsive: true,
    aspectRatio: 2,
    plugins: {
        legend: {
            position: 'bottom',
            labels: {
                boxWidth: 25,
                padding: 10
            }
        },
        tooltip: {
            callbacks: {
                label: (ctx) => {
                    return ` ${ctx.raw.toFixed(1)} minutes`;
                }
            }
        }
    }
};

const COLORS = ['#d70000', '#ff9e01', '#849e00'];
const index = (val) => {
    if (val < 33) return 0; // Red
    if (val < 66) return 1; // Orange
    return 2;               // Green
};
const efficiencyPercentage = computed(() => {
    if (!stats.value['avg_duration']) {
        return 0;
    }
    const value = parseFloat(stats.value['avg_duration']) /
        (parseFloat(stats.value['avg_duration']) + parseFloat(stats.value['avg_idle_time']));
    return (value * 100).toFixed(2);
});

const efficiencyDataset = computed(function () {
    return {
        datasets: [{
            data: [parseFloat(efficiencyPercentage.value), 100 - parseFloat(efficiencyPercentage.value)],
            backgroundColor(ctx) {
                if (ctx.type !== 'data') return;

                // Active slice
                if (ctx.index === 0) return COLORS[index(efficiencyPercentage.value)];

                // Remaining slice (grey background)
                return '#EAEAEA';
            }
        }]
    }
});

const efficiencyChartOptions = {
    aspectRatio: 2,
    circumference: 180,
    rotation: -90
};

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    } else {
        fetchReport();
        fetchAssetList();
        fetchDriversList();
    }
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companiesList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetList = () => {
    return send('get', `assets/list`, {company: formData['company']})
        .then((response) => {
            assetsList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Assets List could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    send('get', `companies/drivers?list=true`, {company: formData['company']})
        .then((response) => {
            driversList.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const fetchReport = () => {
    loading.value = true;
    send('get', `reports/driver-report`, {...formData, page: currentPage.value})
        .then((response) => {
            stats.value = response.data.data.stats;
            reportData.value = response.data.data.reportData.data;
            totalPages.value = response.data.data.reportData.last_page;
            currentPage.value = response.data.data.reportData.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Fuel Refill report could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
</script>
<style scoped>
.efficiency-center-label {
    position: absolute;
    bottom: 10%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
}

@media only screen and (min-width: 1920px) {
    .efficiency-center-label {
        bottom: 0;
    }
}
</style>
